// AttendanceRegister.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { getFacultyAssignedClassNames, filterClassesForFaculty } from '../../utils/facultyHierarchyUtils';
import {
  getStoredAttendance,
  getStoredCorrections,
  saveStoredCorrections,
  getStoredSettings,
  DEFAULT_CLASSES,
  DEFAULT_SUBJECTS,
  DEFAULT_STUDENTS,
  seedLiveRosterHistory
} from './attendanceSeed';
import { fetchAttendanceRecords, fetchAllAttendanceRecords } from '../../services/attendanceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AttendanceRegister.css';
// Icons
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

const mapStatusFromBackend = (status) => {
  switch (status) {
    case 'PRESENT': return 'Present';
    case 'ABSENT': return 'Absent';
    case 'LATE': return 'Late';
    case 'ON_LEAVE': return 'Leave';
    default: return status || 'Present';
  }
};

const formatLocalDate = (dateVal) => {
  if (!dateVal) return '';
  const pad = (num) => num < 10 ? `0${num}` : `${num}`;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  if (typeof dateVal === 'object') {
    const year = dateVal.year;
    const month = dateVal.monthValue || dateVal.month;
    const day = dateVal.dayOfMonth || dateVal.day;
    if (year && month && day) {
      let m = month;
      if (typeof month === 'string') {
        const months = {
          JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
          JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
        };
        m = months[month.toUpperCase()] || 1;
      }
      return `${year}-${pad(m)}-${pad(day)}`;
    }
  }
  if (typeof dateVal === 'string') {
    return dateVal.split('T')[0];
  }
  return dateVal.toString();
};

const AttendanceRegister = ({ orgId }) => {
  const { axios, token, userRole, user } = useAuth();
  const isAdmin = userRole === 'ADMIN';

  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  // State Filters
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedMonthYear, setSelectedMonthYear] = useState(() => {
    const m = new Date().getMonth() + 1;
    const mStr = m < 10 ? `0${m}` : `${m}`;
    const y = new Date().getFullYear().toString();
    return `${y}-${mStr}`;
  });

  const [selectedYear, selectedMonth] = useMemo(() => {
    if (!selectedMonthYear) return ['', ''];
    const [y, m] = selectedMonthYear.split('-');
    return [y, m];
  }, [selectedMonthYear]);

  // Dropdown options
  const [classesList, setClassesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});

  const uniqueCenters = useMemo(() => {
    if (isCustomOrg) {
      return [...new Set(classesList.map(c => c.label || c.name || c.className).filter(Boolean))];
    }
    return [...new Set(classesList.map(c => c.sectionName || c.label || c.name).filter(Boolean))];
  }, [classesList, isCustomOrg]);

  useEffect(() => {
    if (uniqueCenters.length > 0) {
      if (!selectedCenter || !uniqueCenters.includes(selectedCenter)) {
        setSelectedCenter(uniqueCenters[0]);
      }
    }
  }, [uniqueCenters, selectedCenter]);

  const matchingClasses = useMemo(() => {
    if (isCustomOrg) {
      return classesList.filter(c => (c.label || c.name || c.className) === selectedCenter);
    } else {
      const cls = classesList.find(c => c.classId === selectedClassId);
      return cls ? [cls] : [];
    }
  }, [isCustomOrg, classesList, selectedCenter, selectedClassId]);

  // Search/Loading
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Correction Trigger Modal
  const [activeCellModal, setActiveCellModal] = useState(null); // { student, date, status, recordKey }
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionNewStatus, setCorrectionNewStatus] = useState('Present');

  // monthsList and yearsList removed since they are consolidated into month-year input

  // Calculate days in the selected month
  const getDaysInMonth = (monthStr, yearStr) => {
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    return new Date(year, month, 0).getDate();
  };

  const daysInMonthCount = useMemo(() => {
    return getDaysInMonth(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  // Construct dates list for table columns
  const datesArray = useMemo(() => {
    const dates = [];
    for (let i = 1; i <= daysInMonthCount; i++) {
      const dayNum = i < 10 ? `0${i}` : `${i}`;
      const dateStr = `${selectedYear}-${selectedMonth}-${dayNum}`;
      const dateObj = new Date(selectedYear, parseInt(selectedMonth, 10) - 1, i);
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6; // Sat, Sun

      dates.push({
        dayNum: i,
        dateStr,
        dayName,
        isWeekend
      });
    }
    return dates;
  }, [daysInMonthCount, selectedMonth, selectedYear]);

  // Initial Load
  useEffect(() => {
    const initializePage = async () => {
      setIsLoading(true);
      const loadedSettings = getStoredSettings();
      setSettings(loadedSettings);

      try {
        let classes = [];
        let subjects = [];

        // Fetch live Classes if auth active
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
            const rawClasses = res.data?.items || res.data || [];
            classes = rawClasses.map(c => {
              const centerName = isCustomOrg
                ? (c.className || c.label || c.name || '').trim()
                : (c.name || (c.sectionName && c.sectionName !== 'null' && c.sectionName !== 'undefined' ? `${c.className || ''}-${c.sectionName}` : c.className || '')).trim();
              return {
                classId: c.classId || c.id,
                value: c.classId || c.id,
                className: c.className || centerName,
                sectionName: isCustomOrg ? '' : (c.sectionName || ''),
                name: centerName || 'Unknown Center',
                label: centerName || 'Unknown Center'
              };
            });

            const assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
            classes = filterClassesForFaculty(classes, assignedClassNames);
          } catch (e) {
            console.warn("Failed API classes, using seed fallback", e);
          }
        }

        const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
        if (!isCustomOrg && (!classes || classes.length === 0)) {
          classes = DEFAULT_CLASSES;
        }

        // Fetch live Subjects
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_SUBJECTS_BY_ORG}/${orgId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawSubjects = res.data?.items || res.data || [];
            subjects = rawSubjects.map(sub => ({
              courseCode: sub.courseCode || sub.id,
              courseName: sub.courseDetails?.courseName || sub.name || 'Unknown Subject',
              period: sub.period || 'General'
            }));
          } catch (e) {
            console.warn("Failed API subjects, using seed fallback", e);
          }
        }

        if (!subjects || subjects.length === 0) {
          subjects = DEFAULT_SUBJECTS;
        }

        setClassesList(classes);
        setSubjectsList(subjects);

        const calculatedCenters = [...new Set(classes.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (classes.length > 0) setSelectedClassId(classes[0].classId);
        if (subjects.length > 0) setSelectedSubject(subjects[0].courseCode);

      } catch (err) {
        console.error("Failed initializing Register", err);
        setClassesList(DEFAULT_CLASSES);
        setSubjectsList(DEFAULT_SUBJECTS);
        const calculatedCenters = [...new Set(DEFAULT_CLASSES.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (DEFAULT_CLASSES.length > 0) setSelectedClassId(DEFAULT_CLASSES[0].classId);
        setSelectedSubject(DEFAULT_SUBJECTS[0].courseCode);
      } finally {
        setIsLoading(false);
      }
    };

    initializePage();
  }, [orgId, token, axios]);

  // Load students & attendance history once filters change
  const loadRegisterHistory = useCallback(async () => {
    if (matchingClasses.length === 0 || !selectedSubject) return;

    setIsLoading(true);
    try {
      // 1. Fetch Students for all matching classes in parallel
      const studentPromises = matchingClasses.map(async (cls) => {
        let studentList = [];
        if (token) {
          try {
            const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawStudents = res.data?.items || res.data || [];
            studentList = rawStudents.map(item => {
              if (item.id && item.name && item.admissionNumber) {
                return { ...item, classId: cls.classId, className: cls.name };
              }
              const userObj = item.user || {};
              const studentInfoObj = item.studentInfo || {};
              return {
                id: userObj.userId || item.id || '',
                name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || item.name || 'Unknown Student',
                admissionNumber: studentInfoObj.admissionId || item.admissionNumber || 'N/A',
                active: userObj.active !== false && userObj.status !== 'Inactive',
                status: userObj.status || 'Active',
                remark: item.remark || '',
                classId: cls.classId,
                className: cls.name
              };
            });
          } catch (e) {
            console.warn(`Failed API student fetch for class ${cls.classId}`, e);
          }
        }

        if (!studentList || studentList.length === 0) {
          const rawSeed = DEFAULT_STUDENTS[cls.classId] || [];
          studentList = rawSeed.map(item => ({
            id: item.id,
            name: item.name,
            admissionNumber: item.admissionNumber,
            active: item.active !== false && item.status !== 'Inactive',
            status: item.status || 'Active',
            remark: item.remark || '',
            classId: cls.classId,
            className: cls.name
          }));
        }
        return studentList;
      });

      const studentResults = await Promise.all(studentPromises);
      const combinedStudents = studentResults.flat();
      const activeStudents = combinedStudents.filter(s => s.active && s.status !== "Inactive");

      setStudents(activeStudents);

      // 2. Fetch Stored Attendance from backend API for all matching classes
      const dbAttendance = {};
      const attendancePromises = matchingClasses.map(async (cls) => {
        if (token) {
          try {
            const startDateStr = `${selectedYear}-${selectedMonth}-01`;
            const lastDay = daysInMonthCount < 10 ? `0${daysInMonthCount}` : `${daysInMonthCount}`;
            const endDateStr = `${selectedYear}-${selectedMonth}-${lastDay}`;

            const backendResult = await fetchAllAttendanceRecords({
              filterType: 'class',
              id: cls.classId,
              startDate: startDateStr,
              endDate: endDateStr
            });

            const rawBackendList = backendResult || [];
            rawBackendList.forEach(rec => {
              if (rec.userId && rec.attendanceDate) {
                const matchedSubject = subjectsList.find(
                  sub => sub.courseName === rec.subject || sub.courseCode === rec.subject
                );
                const subCode = matchedSubject ? matchedSubject.courseCode : selectedSubject;
                
                const dateStr = formatLocalDate(rec.attendanceDate);
                const key = `${cls.classId}_${dateStr}_${subCode}`;
                
                if (!dbAttendance[key]) {
                  dbAttendance[key] = {
                    classId: cls.classId,
                    date: dateStr,
                    subjectCode: subCode,
                    subjectName: rec.subject || 'General',
                    period: 'General',
                    status: 'final',
                    markedBy: rec.createdBy || 'Teacher Vinay',
                    markedAt: rec.checkInTime || new Date().toISOString(),
                    students: []
                  };
                }
                
                const studentMeta = activeStudents.find(s => s.id === rec.userId);
                dbAttendance[key].students.push({
                  studentId: rec.userId,
                  studentName: studentMeta ? studentMeta.name : 'Unknown Student',
                  admissionNumber: studentMeta ? studentMeta.admissionNumber : 'N/A',
                  status: mapStatusFromBackend(rec.attendanceStatus),
                  remark: rec.remarks || '',
                  attendanceId: rec.attendanceId
                });
              }
            });
          } catch (e) {
            console.warn(`Failed API attendance fetch in Register for class ${cls.classId}`, e);
          }
        }
      });

      await Promise.all(attendancePromises);
      setAttendanceData(dbAttendance);

    } catch (err) {
      console.error("Failed reading Register metrics", err);
      toast.error("Error loading register data.");
    } finally {
      setIsLoading(false);
    }
  }, [matchingClasses, selectedSubject, token, axios, subjectsList, selectedMonth, selectedYear, daysInMonthCount]);

  useEffect(() => {
    loadRegisterHistory();
  }, [loadRegisterHistory]);

  // Match cell data
  const getCellAttendance = (student, dateStr) => {
    const key = `${student.classId}_${dateStr}_${selectedSubject}`;
    const record = attendanceData[key];
    if (!record) return null;

    const studentRecord = record.students.find(s => s.studentId === student.id);
    return studentRecord ? { status: studentRecord.status, remark: studentRecord.remark || '', recordKey: key } : null;
  };

  // Open Cell Click to raise correction
  const handleCellClick = (student, dateObj) => {
    const record = getCellAttendance(student, dateObj.dateStr);
    if (!record) {
      toast.info(`No attendance recorded for ${dateObj.dateStr}.`);
      return;
    }

    setCorrectionReason('');
    setCorrectionNewStatus(record.status);
    setActiveCellModal({
      student,
      date: dateObj.dateStr,
      status: record.status,
      recordKey: record.recordKey
    });
  };

  // Submit cell-triggered correction request
  const submitCellCorrection = (e) => {
    e.preventDefault();
    if (!correctionReason.trim()) {
      toast.error("Please supply a valid reason for this request.");
      return;
    }

    const corrections = getStoredCorrections();
    const classObj = classesList.find(c => c.classId === activeCellModal.student.classId);
    const subjectObj = subjectsList.find(s => s.courseCode === selectedSubject);

    const newReq = {
      id: `req_${Date.now()}`,
      recordKey: activeCellModal.recordKey,
      classId: activeCellModal.student.classId,
      className: classObj?.name || (isCustomOrg ? "Center" : "Class"),
      date: activeCellModal.date,
      subjectCode: selectedSubject,
      subjectName: subjectObj?.courseName || selectedSubject,
      studentId: activeCellModal.student.id,
      studentName: activeCellModal.student.name,
      admissionNumber: activeCellModal.student.admissionNumber,
      oldStatus: activeCellModal.status,
      newStatus: correctionNewStatus,
      reason: correctionReason.trim(),
      requestedBy: `${user?.displayName || user?.name || "Teacher Vinay"} (${userRole})`,
      requestedAt: new Date().toISOString(),
      status: "Pending"
    };

    corrections.unshift(newReq);
    saveStoredCorrections(corrections);

    setActiveCellModal(null);
    toast.success("Correction request successfully filed for Admin review.");
  };

  // Filter students based on search
  const filteredStudents = useMemo(() => {
    return students.filter(s =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.admissionNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [students, searchQuery]);

  // Aggregate Metrics Calculations
  const calculatedMetrics = useMemo(() => {
    if (students.length === 0) return { avgPercentage: 0, totalAbsences: 0, defaultersCount: 0, activeDays: 0 };

    let totalPossibleDays = 0;
    let totalPresentDays = 0;
    let totalAbsences = 0;
    let defaultersCount = 0;
    const activeDates = new Set();
    
    const threshold = settings?.defaulterThreshold || 75;

    // Build stats per student
    const studentStats = students.map(student => {
      let present = 0;
      let absent = 0;
      let late = 0;
      let leave = 0;
      let total = 0;

      datesArray.forEach(dateObj => {
        if (dateObj.isWeekend) return; // Skip weekends in denominator
        
        const cell = getCellAttendance(student, dateObj.dateStr);
        if (cell) {
          total++;
          activeDates.add(dateObj.dateStr);
          
          if (cell.status === 'Present') present++;
          else if (cell.status === 'Absent') absent++;
          else if (cell.status === 'Late') late++;
          else if (cell.status === 'Leave') leave++;
        }
      });

      const effectivePresent = present + late;
      const percentage = total > 0 ? Math.round(((effectivePresent + leave) / total) * 100) : 100;

      if (total > 0 && percentage < threshold) {
        defaultersCount++;
      }

      totalPossibleDays += total;
      totalPresentDays += effectivePresent + leave;
      totalAbsences += absent;

      return { percentage, present, absent, total };
    });

    const avgPercentage = totalPossibleDays > 0 
      ? Math.round((totalPresentDays / totalPossibleDays) * 100)
      : 100;

    return {
      avgPercentage,
      totalAbsences,
      defaultersCount,
      activeDays: activeDates.size
    };
  }, [students, datesArray, attendanceData, selectedCenter, selectedClassId, selectedSubject, settings, isCustomOrg]);

  return (
    <div className="register-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="register-header">
        <div>
          <h1>Attendance Register</h1>
          <p>
            {isCustomOrg
              ? "Monthly grid sheet view showing center-wide attendance ledgers."
              : "Monthly grid sheet view showing class-wide attendance ledgers."
            }
          </p>
        </div>
        <span className="role-badge">{userRole} view</span>
      </div>

      {/* Top Overview Row */}
      <div className="register-summary-row">
        <div className="register-summary-card">
          <div className="card-icon-wrap avg"><InsertChartIcon /></div>
          <div className="card-info">
            <h3>{calculatedMetrics.avgPercentage}%</h3>
            <p>{isCustomOrg ? "CENTER AVERAGE" : "CLASS AVERAGE"}</p>
          </div>
        </div>

        <div className="register-summary-card">
          <div className="card-icon-wrap abs"><ErrorOutlineIcon /></div>
          <div className="card-info">
            <h3>{calculatedMetrics.totalAbsences}</h3>
            <p>ABSENTEE COUNTS</p>
          </div>
        </div>

        <div className="register-summary-card">
          <div className="card-icon-wrap def"><PeopleIcon /></div>
          <div className="card-info">
            <h3>{calculatedMetrics.defaultersCount}</h3>
            <p>DEFAULTER STUDENTS (&lt;{settings?.defaulterThreshold || 75}%)</p>
          </div>
        </div>

        <div className="register-summary-card">
          <div className="card-icon-wrap days"><PlaylistAddCheckIcon /></div>
          <div className="card-info">
            <h3>{calculatedMetrics.activeDays} Days</h3>
            <p>ACTIVE DAYS RECORDED</p>
          </div>
        </div>
      </div>

      {/* Filters & Control bar */}
      <div className="filters-card" style={{ marginBottom: '20px' }}>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Select Month & Year</label>
            <input
              type="month"
              className="filter-control"
              value={selectedMonthYear}
              onChange={(e) => setSelectedMonthYear(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{isCustomOrg ? "KV Center" : "Class"}</label>
            {isCustomOrg ? (
              <select
                className="filter-control"
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
              >
                {uniqueCenters.map(center => (
                  <option key={center} value={center}>{center}</option>
                ))}
              </select>
            ) : (
              <select
                className="filter-control"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                {classesList.map(cls => (
                  <option key={cls.classId} value={cls.classId}>{cls.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="filter-group">
            <label>Subject / Period</label>
            <select
              className="filter-control"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              {subjectsList.map(s => (
                <option key={s.courseCode} value={s.courseCode}>{s.courseName} - {s.period || 'General'}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Grid Ledger Ledger */}
      <div className="register-grid-card">
        <div className="grid-card-header">
          <div>
            <h2>Register Ledger Sheet</h2>
            <p>Click any colored record node to request adjustments or corrections.</p>
          </div>

          <div className="search-box">
            <span className="search-icon-pos"><SearchIcon /></span>
            <input
              type="text"
              placeholder="Search student..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Color Legends */}
        <div className="legend-container">
          <div className="legend-item"><span className="legend-dot p"></span> Present (P)</div>
          <div className="legend-item"><span className="legend-dot a"></span> Absent (A)</div>
          <div className="legend-item"><span className="legend-dot l"></span> Late (L)</div>
          <div className="legend-item"><span className="legend-dot v"></span> Excused Leave (LV)</div>
          <div className="legend-item"><span className="legend-dot empty"></span> Unrecorded (-)</div>
        </div>

        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Synchronizing attendance records...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="empty-container">
            <CalendarMonthIcon />
            <h3>No Students Listed</h3>
            <p>
              {isCustomOrg
                ? "Check KV Center selections or populate students."
                : "Check Class selections or populate students."
              }
            </p>
          </div>
        ) : (
          <div className="register-table-wrapper">
            <table className="register-table">
              <thead>
                <tr>
                  <th className="student-col">Student Name</th>
                  {datesArray.map(dateObj => (
                    <th
                      key={dateObj.dayNum}
                      className={`day-header ${dateObj.isWeekend ? 'weekend' : ''}`}
                      title={dateObj.dateStr}
                    >
                      <span className="day-num">{dateObj.dayNum}</span>
                      <span className="day-name">{dateObj.dayName.slice(0, 2)}</span>
                    </th>
                  ))}
                  <th className="stats-col">P</th>
                  <th className="stats-col">A</th>
                  <th className="stats-col">%</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map(student => {
                  let pCount = 0;
                  let aCount = 0;
                  let lCount = 0;
                  let vCount = 0;
                  let totalMarked = 0;

                  return (
                    <tr key={student.id}>
                      <td className="student-col">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{student.name}</span>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{student.admissionNumber}</span>
                        </div>
                      </td>
                      {datesArray.map(dateObj => {
                        const cell = getCellAttendance(student, dateObj.dateStr);
                        
                        let letter = '';
                        let typeClass = 'empty';
                        
                        if (cell) {
                          totalMarked++;
                          if (cell.status === 'Present') {
                            letter = 'P';
                            typeClass = 'p';
                            pCount++;
                          } else if (cell.status === 'Absent') {
                            letter = 'A';
                            typeClass = 'a';
                            aCount++;
                          } else if (cell.status === 'Late') {
                            letter = 'L';
                            typeClass = 'l';
                            lCount++;
                          } else if (cell.status === 'Leave') {
                            letter = 'LV';
                            typeClass = 'v';
                            vCount++;
                          }
                        } else {
                          letter = '-';
                        }

                        return (
                          <td
                            key={dateObj.dayNum}
                            className={`day-cell ${dateObj.isWeekend ? 'weekend' : ''}`}
                          >
                            <div
                              className={`status-square ${typeClass}`}
                              title={cell ? `Status: ${cell.status}\nRemark: ${cell.remark || 'N/A'}` : 'No Record'}
                              onClick={() => cell && handleCellClick(student, dateObj)}
                            >
                              {letter}
                            </div>
                          </td>
                        );
                      })}
                      {/* Stats */}
                      <td className="stats-col" style={{ color: '#059669' }}>{pCount + lCount}</td>
                      <td className="stats-col" style={{ color: '#ef4444' }}>{aCount}</td>
                      <td
                        className="stats-col"
                        style={{
                          color: (totalMarked > 0 ? Math.round(((pCount + lCount + vCount) / totalMarked) * 100) : 100) < (settings?.defaulterThreshold || 75) ? '#ef4444' : '#1e293b'
                        }}
                      >
                        {totalMarked > 0 ? Math.round(((pCount + lCount + vCount) / totalMarked) * 100) : 100}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Individual Cell Click Adjustment Request Modal */}
      {activeCellModal && (
        <div className="correction-modal-backdrop">
          <div className="correction-modal">
            <div className="modal-header">
              <h3>File Adjustment Request</h3>
              <button className="close-modal-btn" onClick={() => setActiveCellModal(null)}>
                <CloseIcon />
              </button>
            </div>
            <form onSubmit={submitCellCorrection}>
              <div className="modal-body">
                <div className="form-group-modal">
                  <label>Student</label>
                  <input type="text" value={`${activeCellModal.student.name} (${activeCellModal.student.admissionNumber})`} disabled />
                </div>
                <div className="form-group-modal">
                  <label>Date & Subject</label>
                  <input type="text" value={`${activeCellModal.date} - ${subjectsList.find(s => s.courseCode === selectedSubject)?.courseName || selectedSubject}`} disabled />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group-modal">
                    <label>Original Status</label>
                    <input type="text" value={activeCellModal.status} disabled />
                  </div>
                  <div className="form-group-modal">
                    <label>Proposed Correct Status</label>
                    <select
                      value={correctionNewStatus}
                      onChange={(e) => setCorrectionNewStatus(e.target.value)}
                    >
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Late">Late</option>
                      <option value="Leave">Leave</option>
                    </select>
                  </div>
                </div>
                <div className="form-group-modal">
                  <label>Reason for adjustment request</label>
                  <textarea
                    required
                    placeholder="Provide reason (e.g. Student was marked absent incorrectly by sub-teacher during sports class...)"
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-draft" style={{ padding: '8px 16px' }} onClick={() => setActiveCellModal(null)}>
                  Close
                </button>
                <button type="submit" className="btn-submit" style={{ padding: '8px 20px', backgroundColor: '#3b82f6' }}>
                  Submit Correction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRegister;
